import { useMutation, useQuery } from '@tanstack/react-query'
import { Button, EmptyState, PdfIcon, ReportIcon, Select, TabBar, Text } from '@viapeople/components'
import { useEffect, useMemo, useState } from 'react'
import { sessionStore } from '../../sharedStores/sessionStore'
import { useToast } from '../../utils/ToastContext'
import { trpc } from '../../utils/trpc'
import { NumberFieldReports } from '../cycles/cycle-execution/reports/NumberFieldReports'
import { RatingReports } from '../cycles/cycle-execution/reports/RatingReports'
import { SelectFieldReports } from '../cycles/cycle-execution/reports/SelectFieldReports'
import { TextAreaReports } from '../cycles/cycle-execution/reports/TextAreaReports'
import { TextFieldReports } from '../cycles/cycle-execution/reports/TextFieldReports'
import { ConfigureReports } from './ConfigureReports'
import type { FormComponentLite, ReportComponentData, ReportConfig, RoleGroup } from './types'

const renderReportComponent = (componentData: ReportComponentData) => {
	const { component, reports } = componentData

	switch (component.name) {
		case 'rating':
			return <RatingReports key={component.id} component={component} reports={reports} />
		case 'textarea':
			return <TextAreaReports key={component.id} component={component} reports={reports} />
		case 'select':
			return <SelectFieldReports key={component.id} component={component} reports={reports} />
		case 'number':
			return <NumberFieldReports key={component.id} component={component} reports={reports} />
		default:
			return <TextFieldReports key={component.id} component={component} reports={reports} />
	}
}

export function ReportsPage() {
	const { user } = sessionStore()
	const { showToast } = useToast()
	const organizationId = user?.organizationId || ''
	const [activeTab, setActiveTab] = useState<'my-reports' | 'configure'>('my-reports')
	const [selectedCycleId, setSelectedCycleId] = useState<string>('')
	const [selectedPhase, setSelectedPhase] = useState<string>('all')
	const [config, setConfig] = useState<ReportConfig>({ includeQuestionIds: [], visibleRoles: [] })

	const [selectedParticipantIds, setSelectedParticipantIds] = useState<string[]>([])
	const [selectedFormIds, setSelectedFormIds] = useState<string[]>([])
	const [questionSearch, setQuestionSearch] = useState<string>('')
	const { data: cyclesData } = useQuery({
		...trpc.client.dashboard.getCyclesInfo.queryOptions(),
		enabled: !!organizationId,
	})

	const cycles = cyclesData || []
	const cycleOptions = cycles.map((c) => ({
		value: c.id,
		label: c.cycleInfo.name || 'Cycle',
	}))

	const serverConfigQuery = useQuery({
		...trpc.client.reports.getConfig.queryOptions({ cycleId: selectedCycleId || '' }),
		enabled: !!selectedCycleId,
	})
	const serverConfig = serverConfigQuery.data
	const isAuthorizedForReports = serverConfigQuery.isSuccess

	const {
		data: formComponents,
		isLoading: formComponentsLoading,
		isError: formComponentsError,
	} = useQuery({
		...trpc.client.reports.getComponentsByCycle.queryOptions({ cycleId: selectedCycleId || '' }),
		enabled: !!selectedCycleId && !!isAuthorizedForReports,
	})

	useEffect(() => {
		if (!selectedCycleId) return
		const saved = localStorage.getItem(`report-config:${selectedCycleId}`)
		if (saved) {
			try {
				const parsed = JSON.parse(saved) as ReportConfig
				setConfig({
					includeQuestionIds: parsed.includeQuestionIds || [],
					visibleRoles: parsed.visibleRoles || [],
				})
			} catch {
				setConfig({ includeQuestionIds: [], visibleRoles: [] })
			}
		} else {
			setConfig({ includeQuestionIds: [], visibleRoles: [] })
		}
	}, [selectedCycleId])

	const generatePdfMutation = useMutation({
		...trpc.client.reports.generatePdf.mutationOptions(),
		onSuccess: (data) => {
			if (data?.warning) {
				showToast(data.warning, 'error')
			} else {
				showToast('Report generated successfully')
			}

			if (data?.data && data?.fileName && data?.contentType) {
				const binaryString = atob(data.data)
				const bytes = new Uint8Array(binaryString.length)
				for (let i = 0; i < binaryString.length; i++) {
					bytes[i] = binaryString.charCodeAt(i)
				}

				const blob = new Blob([bytes], {
					type: data.contentType,
				})

				const url = URL.createObjectURL(blob)
				const link = document.createElement('a')
				link.href = url
				link.download = data.fileName
				document.body.appendChild(link)
				link.click()
				document.body.removeChild(link)
				URL.revokeObjectURL(url)
			}
		},
		onError: (error) => {
			showToast(`Failed to generate report: ${error.message}`, 'error')
		},
	})

	const {
		data: reportData,
		isLoading: reportDataLoading,
		refetch: refetchReportData,
	} = useQuery({
		...trpc.client.reports.getMyReportData.queryOptions({ cycleId: selectedCycleId || '' }),
		enabled:
			!!selectedCycleId && !!serverConfig && serverConfig.includeQuestionIds?.length > 0 && !!isAuthorizedForReports,
	})

	const { data: participantsByRole } = useQuery({
		...trpc.client.reports.getParticipantsByRole.queryOptions({ cycleId: selectedCycleId || '' }),
		enabled: !!selectedCycleId && !!isAuthorizedForReports,
	})

	useEffect(() => {
		if (serverConfig && selectedCycleId) {
			setConfig({
				includeQuestionIds: (serverConfig.includeQuestionIds as string[]) || [],
				visibleRoles: (serverConfig.visibleRoles as string[]) || [],
			})
			setSelectedParticipantIds((serverConfig.selectedParticipantIds as string[]) || [])
			setSelectedFormIds((serverConfig.selectedFormIds as string[]) || [])
		}
	}, [serverConfig, selectedCycleId])

	useEffect(() => {
		if (activeTab === 'my-reports' && selectedCycleId && serverConfig) {
			refetchReportData()
		}
	}, [activeTab, selectedCycleId, serverConfig, refetchReportData])

	const roleGroups = (participantsByRole as RoleGroup[]) || []

	// Extract unique phases from report data
	const phaseOptions = useMemo(() => {
		if (!reportData || !reportData.components) return [{ value: 'all', label: 'All Phases' }]

		const phases = new Set<string>()
		reportData.components.forEach((comp) => {
			comp.submissions?.forEach((sub: { label: string }) => {
				// Each submission label represents a phase (e.g., "Self Evaluation", "Group Evaluation - Name")
				phases.add(sub.label)
			})
		})

		const options = [{ value: 'all', label: 'All Phases' }]
		Array.from(phases)
			.sort()
			.forEach((phase) => {
				options.push({ value: phase, label: phase })
			})

		return options
	}, [reportData])

	const transformedReportData: ReportComponentData[] = useMemo(() => {
		if (!reportData?.components || !reportData?.formComponents) return []

		return reportData.components
			.map((comp) => {
				const baseCustom = { label: comp.label, placeholder: '' } as Record<string, unknown>
				const found = (reportData.formComponents as unknown as Array<Record<string, unknown>>)?.find(
					(fc) => (fc as { id: string }).id === comp.id,
				) as Record<string, unknown> | undefined

				const component: Record<string, unknown> = found
					? { ...found }
					: {
							id: comp.id,
							type: 'input',
							name: 'text',
							custom: baseCustom,
						}

				if (!component.custom) {
					component.custom = baseCustom
				}

				if (component.required === undefined) component.required = false
				if (component.permissionTemplateId === undefined) component.permissionTemplateId = ''
				if (component.containerId === undefined) component.containerId = null

				// Filter submissions by selected phase
				const filteredSubmissions =
					selectedPhase === 'all'
						? comp.submissions || []
						: (comp.submissions || []).filter((sub: { label: string }) => sub.label === selectedPhase)

				return {
					id: comp.id,
					component: component as unknown as ReportComponentData['component'],
					reports: [
						{
							label: comp.label,
							submissions: filteredSubmissions,
							average: comp.average,
						},
					],
				} as ReportComponentData
			})
			.filter((item) => (item.reports[0]?.submissions?.length || 0) > 0) // Only show components with responses in selected phase
	}, [reportData, selectedPhase])

	const tabs = [
		{ id: 'my-reports', label: 'My reports' },
		...(isAuthorizedForReports ? [{ id: 'configure', label: 'Configure report' }] : []),
	]

	const renderConfigure = () => (
		<ConfigureReports
			selectedCycleId={selectedCycleId}
			organizationId={organizationId}
			config={config}
			setConfig={setConfig}
			selectedParticipantIds={selectedParticipantIds}
			setSelectedParticipantIds={setSelectedParticipantIds}
			selectedFormIds={selectedFormIds}
			setSelectedFormIds={setSelectedFormIds}
			questionSearch={questionSearch}
			setQuestionSearch={setQuestionSearch}
			formComponents={(formComponents as FormComponentLite[]) || []}
			formComponentsLoading={formComponentsLoading}
			formComponentsError={formComponentsError}
			roleGroups={roleGroups}
			isCycleAdminForSelectedCycle={!!isAuthorizedForReports}
			refetchReportData={refetchReportData}
		/>
	)

	const renderMyReports = () => {
		if (!selectedCycleId) {
			return (
				<div className='p-6'>
					<EmptyState
						icon={PdfIcon}
						title='Select a cycle to view reports'
						description='Choose a cycle above to preview and download your report.'
					/>
				</div>
			)
		}

		return (
			<div className='p-6 space-y-6'>
				<div className='flex items-center justify-between'>
					<div>
						<Text variant='h5' weight='semibold'>
							Report Preview
						</Text>
					</div>
					{reportData?.cycle?.name && (
						<div className='text-right'>
							<Text variant='small' className='text-neutral-600'>
								Cycle
							</Text>
							<Text variant='small' weight='semibold' className='text-neutral-800'>
								{reportData.cycle.name}
							</Text>
						</div>
					)}
				</div>

				{reportDataLoading ? (
					<div className='bg-neutral-50 rounded-lg p-8 text-center'>
						<Text variant='small' className='text-neutral-600'>
							Loading report data...
						</Text>
					</div>
				) : !serverConfig ? (
					<div className='bg-neutral-50 rounded-lg p-8'>
						<EmptyState
							icon={ReportIcon}
							title='Report not configured'
							description={
								isAuthorizedForReports
									? 'Go to the Configure tab to select questions and filters for your report.'
									: "Reports for this cycle haven't been configured yet. Contact a cycle administrator to set up report configuration."
							}
						/>
					</div>
				) : serverConfig.includeQuestionIds?.length === 0 ? (
					<div className='bg-orange-50 rounded-lg p-8'>
						<EmptyState
							icon={ReportIcon}
							title='No questions selected'
							description='Select questions in the Configure tab to include in your report, then save the configuration.'
						/>
					</div>
				) : !reportData || (reportData.components || []).length === 0 ? (
					<div className='bg-neutral-50 rounded-lg p-8'>
						<EmptyState
							icon={ReportIcon}
							title='No data available'
							description='No evaluation data found matching your current filters. Try adjusting your configuration or ensure evaluations have been completed.'
						/>
					</div>
				) : (
					<div className='bg-white border border-neutral-200 rounded-lg shadow-sm'>
						<div className='p-6 border-b border-neutral-200'>
							<Text variant='h4' weight='semibold' className='text-neutral-900'>
								Performance Report
							</Text>
							<Text variant='small' className='text-neutral-600 mt-1'>
								{reportData.cycle?.name} - Generated on {new Date().toLocaleDateString()}
							</Text>
						</div>

						<div className='p-6 space-y-8'>
							{transformedReportData.map((componentData, index) => (
								<div key={componentData.id}>
									{renderReportComponent(componentData)}
									{index < transformedReportData.length - 1 && <div className='border-t border-neutral-200 mt-8'></div>}
								</div>
							))}
						</div>

						{reportData?.signatures && reportData.signatures.length > 0 && (
							<div className='px-6 pb-6'>
								<div className='bg-neutral-50 border-2 border-neutral-200 rounded-lg p-8'>
									<Text variant='h5' weight='semibold' className='text-center mb-6 uppercase tracking-wide'>
										Digital Acknowledgments
									</Text>
									<div className='space-y-4'>
										{reportData.signatures.map((sig) => (
											<div
												key={`${sig.role}-${sig.signedAt}`}
												className='bg-white border border-neutral-200 rounded-lg p-6 shadow-sm'>
												<div className='flex justify-between items-center mb-4 pb-2 border-b border-neutral-100'>
													<Text variant='xsmall' weight='semibold' className='text-neutral-600 uppercase tracking-wide'>
														Digital Signature
													</Text>
													<Text variant='xsmall' className='text-neutral-500'>
														{sig.role} • {new Date(sig.signedAt).toLocaleDateString()}
													</Text>
												</div>
												<div className='text-center py-4 border-b-2 border-primary-500'>
													<Text variant='none' className='font-sacramento text-4xl text-neutral-900'>
														{sig.signedBy}
													</Text>
												</div>
											</div>
										))}
									</div>
								</div>
							</div>
						)}

						<div className='px-6 py-4 bg-neutral-50 border-t border-neutral-200 rounded-b-lg'>
							<div className='flex justify-between items-center'>
								<Button
									data-testid='reports-page-download-btn'
									variant='primary'
									onClick={() => {
										if (selectedCycleId) {
											generatePdfMutation.mutate(
												{
													cycleId: selectedCycleId,
												},
												{
													onSuccess: () => {},
												},
											)
										}
									}}
									disabled={generatePdfMutation.isPending}>
									{generatePdfMutation.isPending ? 'Generating...' : 'Download Report'}
								</Button>
								<Text variant='xsmall' className='text-neutral-500'>
									Report contains {transformedReportData.length} section(s)
								</Text>
							</div>
						</div>
					</div>
				)}
			</div>
		)
	}

	return (
		<main data-testid='reports-page' className='w-full p-6'>
			<div className='mb-6'>
				<Text variant='h3' weight='semibold'>
					Reports
				</Text>
			</div>

			<div className='mb-4 flex items-end gap-3'>
				<div className='min-w-[280px]'>
					<Select
						label='Cycle'
						placeholder={cycleOptions.length ? 'Select cycle' : 'No cycles'}
						options={cycleOptions}
						value={selectedCycleId}
						onChange={(value: string) => setSelectedCycleId(value)}
					/>
				</div>
				{selectedCycleId && phaseOptions.length > 1 && (
					<div className='min-w-[280px]'>
						<Select
							label='Phase'
							placeholder='Select phase'
							options={phaseOptions}
							value={selectedPhase}
							onChange={(value: string) => setSelectedPhase(value)}
						/>
					</div>
				)}
				<div className='flex-1' />
				<TabBar
					tabs={tabs}
					activeTab={activeTab}
					onChange={(tabId: string) => setActiveTab((tabId as 'my-reports' | 'configure') || 'my-reports')}
				/>
			</div>

			<div className='border border-neutral-200 bg-neutral-0 rounded-lg shadow-sm'>
				{activeTab === 'configure' && isAuthorizedForReports ? renderConfigure() : renderMyReports()}
			</div>
		</main>
	)
}
